import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Loader2,
  Shield,
  CreditCard,
  ShoppingCart,
  Check,
  Home,
  ChevronLeft,
  BookOpen,
  MessageCircle,
  Smartphone,
  Infinity as InfinityIcon,
} from "lucide-react";
import PhoneAnchor from "./PhoneAnchor";
import { SERVICE_GRID } from "./StageServices";

const KEY_TO_DEMO_ID = {
  private_chef: "private-chef",
  massage: "massage",
  airport_ride: "airport-pickup",
  arrival_event: "arrival-event",
  wellness_therapy: "wellness-therapy",
  restaurant_reservations: "restaurant-reservations",
  other: "other",
};

export default function StageDecision({
  previewToggles,
  pricing,
  onSubscribe,
  onBack,
  actionLoading,
  hostLogo,
  compactHeight = false,
}) {
  const enabled = useMemo(
    () => SERVICE_GRID.filter((s) => !!previewToggles?.[s.key]),
    [previewToggles]
  );
  const propertyCount = Math.max(Number(pricing?.propertyCount) || 0, 0);

  const PER_PROPERTY_USD = 8;
  const flatMonthlyTotal = propertyCount * PER_PROPERTY_USD;
  const flatPriceLabel = `$${flatMonthlyTotal.toLocaleString("en-US")}`;
  const isQuote = !!pricing?.isQuote;
  const displayedPrice = isQuote ? pricing?.displayPrice : flatPriceLabel;
  const displayedHelper = isQuote
    ? pricing?.helperText
    : `Flat $${PER_PROPERTY_USD} per property per month — no tiers. Includes the Bellhopt Concierge guest app and unlimited request handling.`;

  const enabledServices = useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    enabled.forEach((s) => {
      const id = KEY_TO_DEMO_ID[s.key];
      if (id) set.add(id);
    });
    return set;
  }, [enabled]);

  return (
    <div className="relative w-full laptop:h-full grid laptop:grid-rows-1 laptop:grid-cols-[1fr_1fr] grid-rows-[auto_auto]">
      {/* Left — decision anchor */}
      <div
        className={`relative px-6 laptop:px-14 order-2 laptop:order-1 flex flex-col laptop:min-h-0 laptop:overflow-y-auto ${
          compactHeight
            ? "py-3 laptop:py-3 justify-start laptop:overflow-y-auto"
            : "py-5 laptop:py-6 laptop:justify-center laptop:overflow-visible"
        }`}
      >
        <div className="w-full max-w-[520px] mx-auto laptop:mx-0">
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-2">
            Step 5 · Subscribe
          </p>
          <h2 className="text-[26px] laptop:text-[34px] font-extrabold text-[#111111] leading-[1.04]" style={{ letterSpacing: -0.6 }}>
            You're ready.
            <br />
            <span className="text-[#5F6368]">Add a real concierge desk to every property.</span>
          </h2>
          <p className="mt-4 max-w-[500px] text-[14px] text-[#5F6368] leading-relaxed">
            Bellhopt is not just software. Your guests get a live concierge
            team that takes in requests, curates options, and handles the
            coordination and logistics for every booking.
          </p>

          {/* Anchor card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`rounded-[20px] bg-[#111111] text-white overflow-hidden ${
              compactHeight ? "mt-3 laptop:mt-4" : "mt-5 laptop:mt-7"
            }`}
          >
            <div className={compactHeight ? "p-4 laptop:p-5" : "p-5 laptop:p-6"}>
              <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/70">
                    Your Pricing
                  </span>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#10B981]">
                  <Check size={10} strokeWidth={3} />
                  Always included
                </span>
              </div>

              <div className="flex items-end gap-2 mb-4">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={displayedPrice}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    {isQuote ? (
                      <span
                        className="text-[28px] laptop:text-[38px] font-extrabold leading-none"
                        style={{ letterSpacing: -0.7 }}
                      >
                        {displayedPrice}
                      </span>
                    ) : (
                      <span
                        className="text-[44px] laptop:text-[56px] font-extrabold leading-none tabular-nums"
                        style={{ letterSpacing: -1 }}
                      >
                        {displayedPrice}
                      </span>
                    )}
                  </motion.div>
                </AnimatePresence>
                {!isQuote && (
                  <span className="text-[13px] font-semibold text-white/60 pb-2">/month</span>
                )}
              </div>

              <div className="flex items-center gap-3 text-[11px] text-white/70">
                <span className="flex items-center gap-1"><Home size={11} /> {propertyCount} propert{propertyCount === 1 ? "y" : "ies"}</span>
                <span className="opacity-40">·</span>
                <span>{enabled.length} service{enabled.length === 1 ? "" : "s"}</span>
                {!isQuote && propertyCount > 0 ? (
                  <>
                    <span className="opacity-40">·</span>
                    <span className="tabular-nums">
                      {propertyCount} × ${PER_PROPERTY_USD} = {flatPriceLabel}/mo
                    </span>
                  </>
                ) : null}
              </div>

              <p className="mt-3 text-[11px] text-white/70">
                {displayedHelper}
              </p>

              <div className={`rounded-[14px] bg-white/5 border border-white/10 ${compactHeight ? "mt-3 p-2.5" : "mt-4 p-3"}`}>
                <p className="text-[11px] text-white/80 leading-relaxed">
                  Think of this as a guest-facing front desk for your
                  portfolio. Real Bellhopt concierges respond, coordinate, and
                  follow through on requests so your team does not have to.
                </p>
              </div>

              {/* Always included chips — platform features every plan gets */}
              <div className={`flex flex-wrap items-center gap-1.5 ${compactHeight ? "mt-3" : "mt-4"}`}>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[10px] font-semibold text-white">
                  <Smartphone size={10} strokeWidth={2} />
                  Bellhopt Concierge Guest App
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[10px] font-semibold text-white">
                  <InfinityIcon size={10} strokeWidth={2} />
                  Unlimited Request Handling
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[10px] font-semibold text-white">
                  <MessageCircle size={10} strokeWidth={2} />
                  AI + Human Concierge
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[10px] font-semibold text-white">
                  <BookOpen size={10} strokeWidth={2} />
                  Branded Guidebook
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[10px] font-semibold text-white">
                  <ShoppingCart size={10} strokeWidth={2} />
                  Pre-Arrival Groceries
                </span>
              </div>
            </div>

            {/* Selected services reel */}
            {enabled.length > 0 && (
              <div className={compactHeight ? "px-4 laptop:px-5 pb-4 laptop:pb-5 -mt-1" : "px-5 laptop:px-6 pb-5 laptop:pb-6 -mt-1"}>
                <div className="flex flex-wrap gap-1.5">
                  {enabled.slice(0, 8).map((s) => {
                    const Icon = s.icon;
                    return (
                      <span
                        key={s.key}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/10 border border-white/10 text-[11px] font-semibold text-white"
                      >
                        <Icon size={11} strokeWidth={2} />
                        {s.name}
                      </span>
                    );
                  })}
                  {enabled.length > 8 && (
                    <span className="inline-flex items-center px-2.5 py-1.5 rounded-full bg-white/10 text-[11px] font-semibold text-white/80">
                      +{enabled.length - 8} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* CTA bar */}
            <div
              className={`bg-white/5 flex items-center justify-between gap-3 flex-wrap border-t border-white/10 ${
                compactHeight ? "px-4 laptop:px-5 py-3" : "px-5 laptop:px-6 py-4"
              }`}
            >
              <div className="flex items-center gap-3 text-[11px] text-white/70">
                <span className="flex items-center gap-1"><Shield size={11} /> {isQuote ? "Custom onboarding" : "Cancel anytime"}</span>
                <span className="flex items-center gap-1"><CreditCard size={11} /> {isQuote ? "Team follow-up by email" : "Secure checkout"}</span>
              </div>
              <button
                onClick={() => onSubscribe("stage_decision")}
                disabled={actionLoading || enabled.length === 0}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-[12px] bg-[#EF4949] text-white text-[14px] font-bold hover:bg-[#DC2626] transition-colors shadow-[0_8px_32px_rgba(239,73,73,0.2)] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : enabled.length === 0 ? (
                  "Select at least one service"
                ) : (
                  <>
                    {pricing.ctaLabel || "Subscribe & go live"}
                    <ArrowRight size={14} strokeWidth={2.5} />
                  </>
                )}
              </button>
            </div>
          </motion.div>

          <button
            onClick={onBack}
            className={`inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#888888] hover:text-[#111111] transition-colors ${
              compactHeight ? "mt-3" : "mt-4"
            }`}
          >
            <ChevronLeft size={13} />
            Rethink my selections
          </button>
        </div>
      </div>

      {/* Right — phone anchor */}
      <div className="relative order-1 laptop:order-2 overflow-hidden laptop:min-h-0 h-[360px] tabletOnly:h-[420px] laptop:h-auto">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(500px 400px at 50% 45%, rgba(239,73,73,0.1), transparent 60%)",
          }}
        />
        <div
          className={`relative h-full flex items-center justify-center ${
            compactHeight ? "py-1 laptop:py-4" : "py-2 laptop:py-6"
          }`}
        >
          <PhoneAnchor
            stage={5}
            enabledServices={enabledServices}
            hostLogo={hostLogo}
            compactHeight={compactHeight}
          />
        </div>
      </div>
    </div>
  );
}
